import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Button } from '@goaltree/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@goaltree/ui/components/dialog'
import { useMutation } from 'convex/react'
import { Globe } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { patchGoal } from '@/lib/goals-optimistic'

// A shared goal link only resolves when the goal is public, so sharing a private
// goal first prompts the owner to flip it public, then runs the original action.
export function MakePublicDialog({
  open,
  onOpenChange,
  goalId,
  onMadePublic,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  goalId: Id<'nodes'>
  onMadePublic: () => void | Promise<void>
}) {
  const { t } = useTranslation()
  const setIsPublic = useMutation(api.nodes.setIsPublic).withOptimisticUpdate(
    (store, { goalId, isPublic }) => patchGoal(store, goalId, { isPublic }),
  )
  const [pending, setPending] = useState(false)

  async function handleConfirm() {
    setPending(true)
    try {
      await setIsPublic({ goalId, isPublic: true })
      onOpenChange(false)
      await onMadePublic()
    } catch {
      toast.error(t('common.visibilityUpdateError'))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('common.sharePrivateTitle')}</DialogTitle>
          <DialogDescription>{t('common.sharePrivateDesc')}</DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 cursor-pointer"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={pending} className="flex-1 cursor-pointer">
            <Globe className="h-3.5 w-3.5" />
            {t('common.makePublicAndShare')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
